import { useState, useCallback } from 'react';

const MAX_HISTORY_SIZE = 15;
const STORAGE_KEY = 'createWith_color_history_1';

/**
 * Custom hook to manage color history with FIFO (First In, First Out) behavior
 * - Maintains a maximum of 10 colors
 * - New colors are added to the front
 * - Selecting an existing color moves it to the front
 * - Persists history to localStorage
 */
export const useColorHistory = () => {
	// Initialize with colors from localStorage or default colors
	const getInitialHistory = (): string[] => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				return Array.isArray(parsed) ? parsed.slice(0, MAX_HISTORY_SIZE) : getDefaultColors();
			}
		} catch (error) {
			console.warn('Failed to load color history from localStorage:', error);
		}
		return getDefaultColors();
	};

	// Default colors to start with - only white initially
	const getDefaultColors = (): string[] => [
		// '#FFFFFF', // White - initial color
	];

	const [colorHistory, setColorHistory] = useState<string[]>(getInitialHistory());

	// Save to localStorage
	const saveToStorage = useCallback((colors: string[]) => {
		try {
			// localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
		} catch (error) {
			console.warn('Failed to save color history to localStorage:', error);
		}
	}, []);

	// Add a new color to history (or move existing color to front)
	const addColorToHistory = useCallback(
		(color: string) => {
			// Normalize color to uppercase hex format
			const normalizedColor = color.toUpperCase();

			setColorHistory(prevHistory => {
				// Remove the color if it already exists
				const filteredHistory = prevHistory.filter(c => c !== normalizedColor);

				// Add the color to the front
				const newHistory = [normalizedColor, ...filteredHistory].slice(0, MAX_HISTORY_SIZE);

				// Save to localStorage
				saveToStorage(newHistory);

				return newHistory;
			});
		},
		[saveToStorage],
	);

	// Select a color from history (moves it to front)
	const selectColorFromHistory = useCallback(
		(color: string) => {
			addColorToHistory(color);
			return color;
		},
		[addColorToHistory],
	);

	// Clear all history and reset to defaults
	const clearHistory = useCallback(() => {
		const defaultColors = getDefaultColors();
		setColorHistory(defaultColors);
		saveToStorage(defaultColors);
	}, [saveToStorage]);

	return {
		colorHistory,
		addColorToHistory,
		selectColorFromHistory,
		clearHistory,
	};
};

export default useColorHistory;
